import { TRIAL_DURATION_TYPES, type PlanTrial } from '@vy/types';
import React, { useMemo } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { PlanSettingsAutoCompleteField } from './PlanSettingsAutoCompleteField';
import type { PlanSettingsSelectItem } from './PlanSettingsSelect';

type MaybePlanTrial = PlanTrial | null | undefined;
type MaybePlanSettingsSelectItem = PlanSettingsSelectItem<MaybePlanTrial>;

type TrialDurationAutoCompleteProps<FormFields extends FieldValues> = {
  className?: string;
  control: Control<FormFields>;
  name: FieldPath<FormFields>;
  defaultValue?: MaybePlanTrial;
  disabled?: boolean;
  freeTrial: boolean;
  label?: string;
};

export const TrialDurationAutoComplete = <FormFields extends FieldValues>({
  className,
  control,
  name,
  defaultValue,
  disabled = false,
  freeTrial,
  label = 'Trial period',
}: TrialDurationAutoCompleteProps<FormFields>) => {
  const defaultValueAsItem = useMemo(
    () =>
      getTrialDurationValues(freeTrial).find(
        ({ value }) => value?.duration === defaultValue?.duration && value?.type === defaultValue?.type,
      ),
    [defaultValue, freeTrial],
  );

  return (
    <PlanSettingsAutoCompleteField
      control={control}
      name={name}
      className={className}
      values={getTrialDurationValues(freeTrial)}
      defaultItem={defaultValueAsItem ?? getTrialDurationValues(freeTrial)[0]}
      disabled={disabled}
      label={label}
      mapValueChange={(inputNumber) => ({
        duration: inputNumber,
        type: TRIAL_DURATION_TYPES.Months,
        free: freeTrial,
      })}
    />
  );
};

function getTrialDurationValues(freeTrial: boolean): MaybePlanSettingsSelectItem[] {
  const values = [
    {
      label: 'None',
      value: null,
    },
  ];
  const valuesWithWeeks = addWeeksToValues(values, freeTrial);

  return addMonthsToValues(valuesWithWeeks, freeTrial);
}

function addWeeksToValues(
  values: MaybePlanSettingsSelectItem[],
  freeTrial: boolean,
): MaybePlanSettingsSelectItem[] {
  const durationWithWeeks = Array.from({ length: 3 }, (_, i) => i + 1).map<PlanSettingsSelectItem<PlanTrial>>(
    (value) => {
      return {
        label: getWeekLabelByWeekIndex(value),
        value: {
          type: TRIAL_DURATION_TYPES.Weeks,
          duration: value,
          free: freeTrial,
        },
      };
    },
  );

  return [...values, ...durationWithWeeks];
}

function addMonthsToValues(
  values: MaybePlanSettingsSelectItem[],
  freeTrial: boolean,
): MaybePlanSettingsSelectItem[] {
  const durationWithMonths = Array.from({ length: 6 }, (_, i) => i + 1).map<MaybePlanSettingsSelectItem>(
    (value) => {
      return {
        label: getMonthLabelByMonthIndex(value),
        value: {
          type: TRIAL_DURATION_TYPES.Months,
          duration: value,
          free: freeTrial,
        },
      };
    },
  );

  return [...values, ...durationWithMonths];
}

function getMonthLabelByMonthIndex(monthIndex: number) {
  return monthIndex === 1 ? `${monthIndex} month` : `${monthIndex} months`;
}

function getWeekLabelByWeekIndex(weekIndex: number) {
  return weekIndex === 1 ? `${weekIndex} week` : `${weekIndex} weeks`;
}
